import { DeleteConfirmationPopover } from '@/components/DeleteConfirmationPopover';
import { HighlightableText } from '@/components/HighlightableText';
import { HoverDisplay } from '@/components/HoverDisplay';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { RestrictedContentManager } from '@/pages/PatientTimelinePage/RestrictedContentManager';
import { changeResourceConfidentiality } from '@/services/permissions';
import { ConfidentialityLevel } from '@bariendo/lib/constants';
import { ActionIcon, Box, Flex, Group, Menu, Paper, Stack, Text, Tooltip } from '@mantine/core';
import { formatHumanName, getReferenceString } from '@medplum/core';
import { Reference, Resource } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconArrowUpRight, IconDotsVertical, IconEdit, IconLink, IconLock, IconTrash } from '@tabler/icons-react';
import { useCallback, useMemo, useState } from 'react';
import { useTimelineNote } from '@/hooks/useTimelineNote';
import { TimelineNoteEditInput } from '@/components/Timeline/TimelineNotes/TimelineNoteEditInput';
import { StandaloneNoteGroup } from '../types';
import { JSONContent } from '@tiptap/react';
import { DateTimeTooltip } from './DateTimeTooltip';
import { HighlightableItem } from './HighlightableItem';

interface StandaloneNoteTimelineItemProps {
  group: StandaloneNoteGroup;
  highlightRef?: string | null;
  copyDirectLink: (itemReference: string) => Promise<void>;
  hasActiveFilters?: boolean;
  handleNavigateToItem?: (itemReference: string) => void;
  searchTerm?: string;
}

export function StandaloneNoteTimelineItem(props: StandaloneNoteTimelineItemProps) {
  const { group, highlightRef, copyDirectLink, hasActiveFilters = false, handleNavigateToItem, searchTerm } = props;

  const {
    currentUserPermissions: { canEditNote, canAccessRestrictedContent },
  } = useCurrentUser();
  const { isEditingNote, isDeletingNote, editNote, deleteNote } = useTimelineNote();
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const medplum = useMedplum();

  const note = useMemo(() => {
    return group.items[0];
  }, [group]);

  const displayName = useMemo(() => {
    if (!note.sender) return 'Unknown';
    return note.sender.resourceType === 'Organization'
      ? (note.sender as { name: string }).name
      : formatHumanName(note.sender.name?.[0]);
  }, [note]);

  const canEdit = useMemo(() => {
    return canEditNote(note.resource);
  }, [canEditNote, note]);

  const changeResourceRestriction = useCallback(
    async (resourceRef: Reference<Resource>, confidentialityLevel: ConfidentialityLevel) => {
      await changeResourceConfidentiality(medplum, {
        resourceRef,
        confidentialityLevel,
      });
    },
    [medplum]
  );

  const handleEditNote = () => {
    setIsEditing(true);
  };

  const handleSaveEdit = async (noteContent: string, richTextContent?: JSONContent) => {
    const success = await editNote(note.reference, noteContent, richTextContent);
    if (success) {
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleDeleteNote = async () => {
    await deleteNote(note.reference);
    setShowDeleteConfirmation(false);
  };

  if (isEditing) {
    return (
      <HighlightableItem itemRef={getReferenceString(note.reference)!} highlightRef={highlightRef}>
        <TimelineNoteEditInput
          initialContent={note.content || ''}
          initialRichTextContent={note.richTextContent}
          onSave={handleSaveEdit}
          onCancel={handleCancelEdit}
          isLoading={isEditingNote}
        />
      </HighlightableItem>
    );
  }

  const isRestricted = note.confidentiality === ConfidentialityLevel.RESTRICTED;

  return (
    <HighlightableItem itemRef={getReferenceString(note.reference)!} highlightRef={highlightRef}>
      <Paper withBorder p="sm" radius="md" bg="#fffbeb" style={{ borderColor: '#FCECCC' }}>
        <HoverDisplay>
          <Stack gap="0" w="100%">
            <Flex justify="space-between">
              <Group justify="flex-start" align="center" m={0} gap="8">
                <Text size="xs" c="#C6A467">
                  Sticky note
                </Text>
              </Group>

              <Flex gap="xs" align="center">
                <Group gap="xs">
                  {/* Kebab menu */}
                  <HoverDisplay.Target>
                    <DeleteConfirmationPopover
                      onConfirm={handleDeleteNote}
                      isLoading={isDeletingNote}
                      title="Delete this note?"
                      description="This can't be undone."
                      opened={showDeleteConfirmation}
                      onClose={() => setShowDeleteConfirmation(false)}
                    >
                      <Menu shadow="md" width={200} position="bottom-end">
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray" size="sm" style={{ flexShrink: 0 }}>
                            <IconDotsVertical size={24} />
                          </ActionIcon>
                        </Menu.Target>

                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<IconLink size={18} color="#246A7A" />}
                            onClick={() => copyDirectLink(getReferenceString(note.reference)!)}
                          >
                            Copy link
                          </Menu.Item>
                          {!isRestricted && canAccessRestrictedContent && (
                            <Menu.Item
                              leftSection={<IconLock size={18} color="#246A7A" />}
                              onClick={() => changeResourceRestriction(note.reference, ConfidentialityLevel.RESTRICTED)}
                            >
                              Mark as sensitive
                            </Menu.Item>
                          )}
                          {isRestricted && canAccessRestrictedContent && (
                            <Menu.Item
                              leftSection={<IconLock size={18} color="#246A7A" />}
                              onClick={() => changeResourceRestriction(note.reference, ConfidentialityLevel.NORMAL)}
                            >
                              Mark as not sensitive
                            </Menu.Item>
                          )}
                          {canEdit && (
                            <Menu.Item leftSection={<IconEdit size={18} color="#246A7A" />} onClick={handleEditNote}>
                              <Text>Edit</Text>
                            </Menu.Item>
                          )}
                          {canEdit && (
                            <Menu.Item
                              leftSection={<IconTrash size={18} color="var(--mantine-color-red-5)" />}
                              onClick={() => setShowDeleteConfirmation(true)}
                            >
                              <Text c="red.6">Delete</Text>
                            </Menu.Item>
                          )}
                        </Menu.Dropdown>
                      </Menu>
                    </DeleteConfirmationPopover>
                  </HoverDisplay.Target>

                  {hasActiveFilters && handleNavigateToItem && (
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      onClick={() => handleNavigateToItem(getReferenceString(note.reference)!)}
                      title="See full context"
                    >
                      <IconArrowUpRight size={16} />
                    </ActionIcon>
                  )}
                </Group>
              </Flex>
            </Flex>
            <Group gap="xxs">
              <Text>
                <Text component="span" fw={600} pr="xs">
                  {displayName}
                </Text>
              </Text>
              <DateTimeTooltip date={note.timestamp} />

              {isRestricted && (
                <Tooltip label="This note contains sensitive information">
                  <IconLock size={16} />
                </Tooltip>
              )}
            </Group>
            <Group justify="space-between" align="flex-start" mt="xs" mb="xs">
              <Box style={{ flex: 1 }}>
                <RestrictedContentManager isRestricted={note.hiddenPayload}>
                  <HighlightableText
                    text={note.content || ''}
                    searchTerm={searchTerm || ''}
                    richTextContent={note.richTextContent}
                    size="md"
                    style={{ whiteSpace: 'pre-wrap' }}
                  />
                </RestrictedContentManager>
              </Box>
            </Group>
            {note.isEdited && (
              <Text size="xxs" c="dimmed">
                (edited)
              </Text>
            )}
          </Stack>
        </HoverDisplay>
      </Paper>
    </HighlightableItem>
  );
}
